import LegalLayout from '@/components/layout/LegalLayout';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

export default function CookiesPage() {
  useSEO({
    title: 'Cookie Policy | Acodia',
    description: 'Acodia sets no tracking or advertising cookies. We keep you signed in using browser storage; the only cookies are set by Stripe during checkout.',
    path: '/cookies',
    ogImage: `${SITE_URL}/images/og/og-default.png`,
  });

  return (
    <LegalLayout eyebrow="Legal" title="Cookie Policy" lastUpdated="26 May 2026" testId="cookies-page">
      <h2>1. What Cookies Are</h2>
      <p>
        Cookies are small text files placed on your device by a website. Related technologies such as browser
        "local storage" work similarly to remember information between visits. This policy explains what we
        use, why, and how you stay in control. For the full picture of how we handle personal data, see our{' '}
        <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>2. Our Approach — Functional Only</h2>
      <p>
        Acodia takes a deliberately minimal approach. <strong>We do not set any advertising, analytics, or
        cross-site tracking cookies.</strong> We do not use Google Analytics or any similar tracking script,
        and we do not build advertising profiles or track you across the web. Because we do not place
        non-essential cookies of our own, we do not show a cookie consent banner.
      </p>

      <h2>3. How We Keep You Signed In</h2>
      <p>
        Rather than session cookies, Acodia keeps you signed in using your browser's <strong>local
        storage</strong> (an entry named <code>acodia_tokens</code>). This is not a cookie and is not
        transmitted automatically with every request — it stays in your browser and is sent only when the app
        makes an authenticated API call. Cross-site request forgery (CSRF) protection uses a short-lived token
        that the app requests and sends as a request header; it is not stored as a cookie either. Clearing
        your browser storage will sign you out.
      </p>

      <h2>4. Cookies We Use</h2>
      <p>
        The only cookies set when you use Acodia are placed by <strong>Stripe</strong> during the secure
        checkout and payment flow. These are strictly necessary for payment and fraud prevention. We do not
        set any first-party cookies of our own.
      </p>
      <div className="legal-table-wrap">
        <table data-testid="cookies-table">
          <thead>
            <tr><th>Name</th><th>Provider</th><th>Purpose</th><th>Duration</th><th>Category</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>__stripe_mid</td>
              <td>Stripe</td>
              <td>Fraud prevention during payment/checkout</td>
              <td>1 year</td>
              <td>Strictly necessary (third-party)</td>
            </tr>
            <tr>
              <td>__stripe_sid</td>
              <td>Stripe</td>
              <td>Fraud prevention session identifier during checkout</td>
              <td>30 minutes</td>
              <td>Strictly necessary (third-party)</td>
            </tr>
            <tr>
              <td>acodia_tokens</td>
              <td>Acodia (browser local storage, not a cookie)</td>
              <td>Keeps you signed in to your dashboard</td>
              <td>Until you sign out or clear browser storage</td>
              <td>Strictly necessary</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        Stripe's cookies are governed by Stripe's own privacy and cookie notices. We never receive your full
        card details.
      </p>

      <h2>5. What We Do Not Use</h2>
      <ul>
        <li>No advertising or marketing cookies.</li>
        <li>No analytics cookies (no Google Analytics, Plausible, or similar).</li>
        <li>No cross-site behavioural tracking pixels.</li>
        <li>No social media trackers or fingerprinting.</li>
      </ul>

      <h2>6. Managing Cookies and Storage</h2>
      <p>
        You can view, block, and delete cookies and clear local storage in your browser settings (Chrome,
        Safari, Firefox, and Edge all provide these controls under Privacy/Cookies). Blocking the Stripe
        cookies or clearing <code>acodia_tokens</code> may prevent checkout or sign-in from working.
      </p>

      <h2>7. Changes to This Policy</h2>
      <p>
        If we introduce any new cookies or tracking technologies, we will update this page first and, where
        the law requires consent, ask for it before placing them. The date at the top reflects the latest
        revision.
      </p>

      <h2>8. Contact</h2>
      <p>
        Questions about cookies or this policy:{' '}
        <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a>.
      </p>
      <hr />
      <p>Have questions? Email <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a>.</p>
    </LegalLayout>
  );
}
